/**
 * Campaign params and analytics events for landing pages.
 *
 * gtag and fbq (the Meta Pixel) are defined inline in public/index.html and
 * queue until consent loads gtag.js / fbevents.js, so events fired here
 * before consent are held, not lost, and nothing is sent without it.
 */

const isCampaignParam = (key) => /^utm_/i.test(key) || ['gclid', 'fbclid', 'msclkid'].includes(key.toLowerCase());

/** Whether this visit came from a campaign: any utm_* param or an ad click id. */
export const isCampaignVisit = (search = '') => [...new URLSearchParams(search).keys()].some(isCampaignParam);

/** `href` with the visitor's utm_* / click-id params carried over. Params the link already sets win. */
export const withUtm = (href, search = '') => {
    const incoming = [...new URLSearchParams(search)].filter(([key]) => isCampaignParam(key));
    if (!incoming.length || !href || href.startsWith('#')) return href;
    const [beforeHash, hash] = href.split('#');
    const [path, query] = beforeHash.split('?');
    const params = new URLSearchParams(query || '');
    incoming.forEach(([key, value]) => { if (!params.has(key)) params.set(key, value); });
    return `${path}?${params.toString()}${hash ? `#${hash}` : ''}`;
};

export const track = (event, params = {}) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', event, params);
};

/**
 * A page view for a client-side navigation. gtag's `config` (index.html)
 * sends one for the first load only; the site is a single page app, so
 * every route change after that has to say so itself — with the full URL
 * (query string included: ?persona=, ?template=, campaign params) as
 * page_location, so GA sees the same URL a fresh load would. Called by
 * Layout on each location change after the first.
 */
export const trackPageView = ({ pathname = window.location.pathname, search = window.location.search } = {}) => {
    const send = () => {
        track('page_view', {
            page_location: window.location.href,
            page_path: `${pathname}${search}`,
            page_title: document.title,
        });
        // the Meta Pixel's own page view for the same navigation (its base
        // code sent the first, on load)
        if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
    };
    // routes are code-split, so right after a navigation the title is still
    // the last page's: wait for the new page to set its own (the first
    // change to <title>), or a moment, whichever comes first
    const title = document.querySelector('title');
    if (!title || typeof MutationObserver === 'undefined') { send(); return; }
    let done = false;
    const finish = () => { if (done) return; done = true; observer.disconnect(); clearTimeout(timer); send(); };
    const observer = new MutationObserver(finish);
    observer.observe(title, { childList: true, characterData: true, subtree: true });
    const timer = setTimeout(finish, 1500);
};

/**
 * One click on a call to action, wherever a page put a data-cta on it: the
 * section the pill belongs to, its words, and whatever the page knows about
 * the visit (persona, template). The event a landing page's dashboards
 * filter on.
 */
export const trackCtaClick = (section, label, params = {}) => track('cta_click', { section, label, ...params });
